import { DeleteIcon, EraserIcon, SpaceIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { KEY_ROWS, type KeyAction, applyKey } from "./keyboard-keys";

/**
 * Klawiatura ekranowa dla pilota i pada.
 *
 * Każdy klawisz jest zwykłym `<button>`, więc **nawigacja strzałkami obsługuje ją
 * sama** — geometria z `spatial.ts` widzi rzędy klawiszy dokładnie tak jak rzędy
 * kafli, a `Enter` z pada woła `click()`. Nie ma tu ani własnej obsługi klawiszy, ani
 * własnego kursora po siatce; to jest cały powód, dla którego ten plik jest krótki.
 *
 * Klawisze pokazują znak, który wstawiają — małą literę. Wielkie litery na klawiszach
 * nad wpisywanymi małymi wyglądają jak błąd, a wyszukiwanie i tak nie odróżnia
 * wielkości.
 */
export interface OnScreenKeyboardProps {
  value: string;
  onChange: (next: string) => void;
  /**
   * Czy przy pojawieniu się wziąć fokus na pierwszy klawisz.
   *
   * Włączone na ekranie wyszukiwania, bo tam klawiatura JEST sposobem pisania: bez
   * tego pierwsza strzałka po wejściu prowadzi w pierwszy fokusowalny element
   * strony, czyli w szynę nawigacji, a nie w litery.
   */
  autoFocusFirstKey?: boolean;
}

export function OnScreenKeyboard({
  value,
  onChange,
  autoFocusFirstKey = false,
}: OnScreenKeyboardProps) {
  function press(action: KeyAction) {
    onChange(applyKey(value, action));
  }

  return (
    // `fieldset`, a nie `div role="group"`: to samo znaczenie wychodzi z elementu,
    // a nie z atrybutu. Etykieta jest obowiązkowa — grupa bez nazwy nic czytnikowi
    // nie mówi, a klawiatura ekranowa czytana jako „grupa, 44 przyciski" jest szumem.
    <fieldset
      aria-label="Klawiatura ekranowa"
      className="flex w-fit flex-col gap-2 rounded-lg border border-border bg-card/40 p-3"
    >
      {KEY_ROWS.map((row, rowIndex) => (
        <div key={row.join("")} className="flex gap-2">
          {row.map((char, keyIndex) => (
            <Button
              key={char}
              type="button"
              variant="outline"
              autoFocus={autoFocusFirstKey && rowIndex === 0 && keyIndex === 0}
              // Klawisz jest celem dla pilota z trzech metrów, więc nie schodzi
              // poniżej 2,75 rem — tego samego progu, co reguła dotyku w `styles.css`.
              className="size-11 p-0 font-mono text-base"
              onClick={() => press({ kind: "char", char })}
            >
              {char}
            </Button>
          ))}
        </div>
      ))}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-11 flex-1 gap-2"
          onClick={() => press({ kind: "space" })}
        >
          <SpaceIcon aria-hidden="true" />
          Odstęp
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 gap-2"
          onClick={() => press({ kind: "backspace" })}
        >
          <DeleteIcon aria-hidden="true" />
          Usuń
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-11 gap-2"
          // Wyczyszczenie pilotem inaczej znaczy dwadzieścia naciśnięć „Usuń".
          onClick={() => press({ kind: "clear" })}
          disabled={value.length === 0}
        >
          <EraserIcon aria-hidden="true" />
          Wyczyść
        </Button>
      </div>
    </fieldset>
  );
}
